import { Injectable } from '@angular/core';
import { Notification, NotificationAction } from '@lycoris-nubila/ngx-lycoris';
import uniq from 'lodash-es/uniq';
import { Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { NotificationDomain } from 'application/common/model/notification-domain';
import { NotificationConverterService } from 'application/notification/notification-converter.service';
import { DocumentApplicationPort } from 'document/port/document-application.port';
import { DocumentRepositoryPort } from 'document/port/document-repository.port';
import { INotification } from 'domain/notification/notification';
import { NotificationOutput } from 'infrastructure/graphql/messaging/notification/model/notification.output';
import { ModuleApplicationPort } from 'module/port/module-application.port';
import { ModuleRepositoryPort } from 'module/port/module-repository.port';
import { TaskApplicationPort } from 'task/port/task-application.port';
import { TaskRepositoryPort } from 'task/port/task-repository.port';
import { UserApiPort } from 'user/port/user-api.port';
import { UserApplicationPort } from 'user/port/user-application.port';

import { AppPushApiPort } from '../common/port/app-push-api.port';
import { RequestMeetingCmd } from './command/request-meeting.cmd';
import { SendMeetingAlternativeCmd } from './command/send-meeting-alternative.cmd';
import { NotificationApiPort } from './port/notification-api.port';
import { NotificationApplicationPort } from './port/notification-application.port';
import { NotificationRepositoryPort } from './port/notification-repository.port';

@Injectable()
export class NotificationApplicationAdapter
	implements NotificationApplicationPort
{
	constructor(
		private _userApiPort: UserApiPort,
		private _appPushApiPort: AppPushApiPort,
		private _taskRepositoryPort: TaskRepositoryPort,
		private _notificationApiPort: NotificationApiPort,
		private _moduleRepositoryPort: ModuleRepositoryPort,
		private _documentRepositoryPort: DocumentRepositoryPort,
		private _notificationRepositoryPort: NotificationRepositoryPort,
		private _taskApplicationPort: TaskApplicationPort,
		private _moduleApplicationPort: ModuleApplicationPort,
		private _userApplicationPort: UserApplicationPort,
		private _documentApplicationPort: DocumentApplicationPort,
		private _notificationConverterService: NotificationConverterService
	) {
		this._appPushApiPort
			.observeDomainMessage<null>(NotificationDomain.NOTIFICATION)
			.subscribe((message) => this._handleMessage(message));
	}

	public acceptMeeting(notificationId: string): Observable<string> {
		return this._notificationApiPort
			.acceptMeeting(notificationId)
			.pipe(
				tap((_) =>
					this._notificationRepositoryPort.deleteNotification(notificationId)
				)
			);
	}

	public cancelMeetingRequest(notificationId: string): Observable<void> {
		return this._notificationApiPort
			.cancelMeetingRequest(notificationId)
			.pipe(
				tap((_) =>
					this._notificationRepositoryPort.deleteNotification(notificationId)
				)
			);
	}

	public markNotificationRead(id: string): Observable<void> {
		return this._notificationApiPort
			.markNotificationRead(id)
			.pipe(tap((_) => this._notificationRepositoryPort.deleteNotification(id)));
	}

	public fetchNotification(notificationId: string): Observable<string> {
		return this._notificationApiPort.fetchNotification(notificationId).pipe(
			tap((notification) => this._saveNotifications([notification])),
			map((notification) => notification.id)
		);
	}

	public observeNotification(notificationId: string): Observable<INotification> {
		return this._notificationRepositoryPort.observeNotification(notificationId);
	}

	public fetchNotifications(): Observable<string[]> {
		return this._notificationApiPort.fetchNotifications().pipe(
			tap((notifications) => this._saveNotifications(notifications)),
			map((notifications) => notifications.map((notification) => notification.id))
		);
	}

	public observeNotifications(): Observable<INotification[]> {
		return this._notificationRepositoryPort.observeNotifications();
	}

	public rejectMeeting(notificationId: string): Observable<void> {
		return this._notificationApiPort
			.rejectMeeting(notificationId)
			.pipe(
				tap((_) =>
					this._notificationRepositoryPort.deleteNotification(notificationId)
				)
			);
	}

	public requestMeeting(cmd: RequestMeetingCmd): Observable<string> {
		return this._notificationApiPort
			.requestMeeting(cmd)
			.pipe(switchMap((notificationId) => this.fetchNotification(notificationId)));
	}

	public sendMeetingAlternative(
		cmd: SendMeetingAlternativeCmd
	): Observable<string> {
		return this._notificationApiPort.sendMeetingAlternative(cmd).pipe(
			tap((_) =>
				this._notificationRepositoryPort.deleteNotification(cmd.notificationId)
			),
			switchMap((notificationId) => this.fetchNotification(notificationId))
		);
	}

	public markAllNotificationsRead(): Observable<string[]> {
		return this._notificationApiPort
			.markAllNotificationsRead()
			.pipe(
				tap((ids) => this._notificationRepositoryPort.deleteNotifications(ids))
			);
	}

	private _handleMessage<T>(message: Notification<NotificationDomain, T>) {
		switch (message.action) {
			case NotificationAction.ADD:
			case NotificationAction.UPDATE:
				this._notificationApiPort
					.fetchNotification(message.entityId)
					.subscribe((notification) => this._saveNotifications([notification]));
				break;
			case NotificationAction.DELETE:
				this._notificationRepositoryPort.deleteNotification(message.entityId);
				break;
		}
	}

	private _saveNotifications(
		notifications: NotificationOutput[]
	): Promise<void> {
		const usersIds = uniq(
			notifications
				.map((n) => [n.sourceUserId, n.targetUserId, n.meeting?.coachId])
				.reduce((p, c) => [...p, ...c], [])
				.filter((id) => !!id)
		);

		const tasksIds = uniq(
			notifications.map((n) => n.taskId).filter((id) => !!id)
		);

		const modulesIds = uniq(
			notifications.map((n) => n.orgModuleId).filter((id) => !!id)
		);

		const documentsId = uniq(
			notifications.map((n) => n.documentId).filter((id) => !!id)
		);

		return Promise.all([
			this._taskApplicationPort.saveTasks(tasksIds),
			this._userApplicationPort.saveUsers(usersIds),
			this._moduleApplicationPort.saveModules(modulesIds),
			this._documentApplicationPort.fetchDocuments({ documentsId }).toPromise(),
			this._notificationRepositoryPort.saveNotifications(
				this._notificationConverterService.notificationsAsEntities(notifications)
			),
		]).then((_) => null);
	}
}
